import { Controller } from "stimulus";
import AppointmentService from "../entrypoints/src/admin/services/appointment-service";

// Mural da turma — class message board. Posts go through the existing
// online/message endpoint; the server persists + broadcasts to everyone in the
// room (sender included) via OnlineClassroomChannel, which re-dispatches a
// `classroom:message` DOM event. We render purely from that event so a recado
// appears exactly once for all clients (no optimistic duplication).
const AVATAR_COLORS = [
  "#C85A3E", "#3E6F8C", "#4A6B3E", "#7A5C3E",
  "#8C3E6F", "#5A554D", "#46606E", "#6E7A3E"
];

export default class extends Controller {
  static targets = ["list", "count", "input", "notes", "badge"];

  static values = {
    appointmentId: Number,
    displayName: String
  };

  connect() {
    this.onMessage = this.onMessage.bind(this);
    document.addEventListener("classroom:message", this.onMessage);
  }

  disconnect() {
    document.removeEventListener("classroom:message", this.onMessage);
  }

  post(event) {
    // The Mural input lives inside the attendance form on desktop — never submit it.
    if (event) event.preventDefault();
    if (!this.hasInputTarget) return;

    const message = this.inputTarget.value.trim();
    if (!message) return;

    AppointmentService.postMeetingMessage(this.appointmentIdValue, {
      message,
      from: this.displayNameValue
    });
    this.inputTarget.value = "";
  }

  onMessage(event) {
    const data = event.detail || {};
    if (!data.message) return;

    this.renderCard(data.from, data.message);
    this.bumpCount();
  }

  toggleNotes() {
    if (this.hasNotesTarget) this.notesTarget.classList.toggle("is-open");
  }

  // ---------- rendering ----------
  renderCard(from, message) {
    if (!this.hasListTarget) return;

    const card = document.createElement("div");
    card.className = `recado c${this.colorIndex(from)}`;

    const text = document.createElement("div");
    text.className = "recado__text";
    text.textContent = message;

    const foot = document.createElement("div");
    foot.className = "recado__foot";

    const avatar = document.createElement("span");
    avatar.className = "avatar";
    avatar.style.width = "16px";
    avatar.style.height = "16px";
    avatar.style.fontSize = "7px";
    avatar.style.background = this.avatarColor(from);
    avatar.textContent = this.initials(from);

    foot.appendChild(avatar);
    foot.appendChild(document.createTextNode(` ${from || ""}`));

    card.appendChild(text);
    card.appendChild(foot);
    this.listTarget.prepend(card);
  }

  bumpCount() {
    if (this.hasCountTarget) {
      this.countTarget.textContent = (parseInt(this.countTarget.textContent, 10) || 0) + 1;
    }
    if (this.hasBadgeTarget) {
      this.badgeTarget.textContent = (parseInt(this.badgeTarget.textContent, 10) || 0) + 1;
      this.badgeTarget.style.display = "grid";
    }
  }

  // ---------- helpers (mirror AppointmentHelper) ----------
  charSum(seed) {
    return String(seed || "").split("").reduce((sum, char) => sum + char.charCodeAt(0), 0);
  }

  colorIndex(seed) {
    return this.charSum(seed) % 4;
  }

  avatarColor(seed) {
    return AVATAR_COLORS[this.charSum(seed) % AVATAR_COLORS.length];
  }

  initials(name) {
    const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
  }
}
